import { asyncTimeout } from '@caresend/utils';
import { OrderFlowRouteName } from '../../../../router/routes/orderFlow/model.js';
import { getStore } from '../../../helpers/initStore.js';
import { setOrderFlowLoadingMessages } from '../helpers.js';
import { getOrderFlowVisitOverviewStepFailures } from './stepValidation.js';

// ROOT STEPS
var OrderFlowSaveEligibility;
(function (OrderFlowSaveEligibility) {
    /**
     * Save button disabled, with validation failure tooltip.
     */
    OrderFlowSaveEligibility["INELIGIBLE_FOR_SAVE"] = "ineligibleForSave";
    /**
     * Eligible for:
     * - Saving as a draft order
     *
     * May be eligible for:
     * - Scheduling (if not in-office)
     *
     * Not eligible for:
     * - Saving as a full order
     */
    OrderFlowSaveEligibility["ELIGIBLE_FOR_SAVE_AS_DRAFT"] = "eligibleForSaveAsDraft";
    /**
     * Eligible for:
     * - Saving as a draft or full order
     * - Scheduling (if not in-office)
     */
    OrderFlowSaveEligibility["ELIGIBLE_FOR_SAVE_AS_FULL"] = "eligibleForSaveAsFull";
})(OrderFlowSaveEligibility || (OrderFlowSaveEligibility = {}));
var OrderFlowSchedulingEligibility;
(function (OrderFlowSchedulingEligibility) {
    /**
     * Either:
     * - Not enough data for scheduling, or
     * - In-office order
     */
    OrderFlowSchedulingEligibility["INELIGIBLE_FOR_SCHEDULING"] = "ineligibleForScheduling";
    /**
     * Eligible for:
     * - Scheduling
     *
     * May be eligible for:
     * - Saving as a draft order
     * - Saving as a full order
     *
     * Not previously sent for scheduling.
     */
    OrderFlowSchedulingEligibility["ELIGIBLE_FOR_SCHEDULING"] = "eligibleForScheduling";
    /** Eligible for scheduling, but already sent for scheduling */
    OrderFlowSchedulingEligibility["ALREADY_SENT_FOR_SCHEDULING"] = "alreadySentForScheduling";
})(OrderFlowSchedulingEligibility || (OrderFlowSchedulingEligibility = {}));
const copy = {
    draftSaving: {
        title: 'Are you done editing?',
        info: `You're missing information required to send this order for scheduling.
    After saving, the order will not be sent for scheduling until all missing
    information is provided. You can still make further changes to the order.`,
        infoForOfflineScheduling: `You are missing information required to send this order to ExamOne.
    After saving this order will not be sent to ExamOne until all missing
    information is provided. You can still make further changes to the order.`,
        cancelButtonText: 'Continue editing',
        confirmButtonText: 'Yes, save draft order',
    },
    queue: {
        title: {
            scheduling: 'Queue this order for scheduling?',
        },
    },
    savingFullOrder: {
        title: 'Are you done editing?',
        info: `If you're done editing, you will not be able to make further changes
      to the order.`,
        cancelButtonText: 'No, only save as draft',
    },
};
const getOrderFlowSaveEligibility = () => {
    const store = getStore();
    const isOrderInvalid = !!getOrderFlowVisitOverviewStepFailures();
    if (isOrderInvalid)
        return OrderFlowSaveEligibility.INELIGIBLE_FOR_SAVE;
    const isOrderIncomplete = store.getters['orderFlow/getIsOrderIncomplete'];
    if (isOrderIncomplete)
        return OrderFlowSaveEligibility.ELIGIBLE_FOR_SAVE_AS_DRAFT;
    return OrderFlowSaveEligibility.ELIGIBLE_FOR_SAVE_AS_FULL;
};
const getOrderFlowSchedulingEligibility = () => {
    var _a, _b, _c, _d, _e;
    const store = getStore();
    const previousReadyForScheduling = !!((_b = (_a = store.state.orderFlow) === null || _a === void 0 ? void 0 : _a.draftOrder) === null || _b === void 0 ? void 0 : _b.readyForScheduling);
    const previousQueuedForScheduling = !!((_d = (_c = store.state.orderFlow) === null || _c === void 0 ? void 0 : _c.draftOrder) === null || _d === void 0 ? void 0 : _d.queuedForScheduling);
    const isOrderScheduleFriendly = store.getters['orderFlow/getIsOrderScheduleFriendly'];
    const isOrderIncomplete = store.getters['orderFlow/getIsOrderIncomplete'];
    const orderID = (_e = store.state.orderFlow.draftOrder) === null || _e === void 0 ? void 0 : _e.id;
    if (!orderID)
        return OrderFlowSchedulingEligibility.INELIGIBLE_FOR_SCHEDULING;
    // If order is incomplete (draft), it's not eligible for scheduling
    if (isOrderIncomplete)
        return OrderFlowSchedulingEligibility.INELIGIBLE_FOR_SCHEDULING;
    if (isOrderScheduleFriendly && previousReadyForScheduling) {
        return OrderFlowSchedulingEligibility.ALREADY_SENT_FOR_SCHEDULING;
    }
    if (!isOrderScheduleFriendly && previousQueuedForScheduling) {
        return OrderFlowSchedulingEligibility.ALREADY_SENT_FOR_SCHEDULING;
    }
    if (!isOrderScheduleFriendly) {
        return OrderFlowSchedulingEligibility.INELIGIBLE_FOR_SCHEDULING;
    }
    return OrderFlowSchedulingEligibility.ELIGIBLE_FOR_SCHEDULING;
};
const getOrderFlowValidationEligibilities = () => ({
    save: getOrderFlowSaveEligibility(),
    schedule: getOrderFlowSchedulingEligibility(),
});
const getOrderFlowLastStepSaveCopy = () => {
    const { save } = getOrderFlowValidationEligibilities();
    if (save === OrderFlowSaveEligibility.ELIGIBLE_FOR_SAVE_AS_DRAFT)
        return 'Save as draft';
    return 'Save';
};
const saveOrder = async (skipPostCreationActions, actions = {}) => {
    const store = getStore();
    const { save, schedule, } = getOrderFlowValidationEligibilities();
    let forceSaveAsDraft = false;
    /** Create or update draft order, or create full order. */
    const saveMaybeDraftOrder = async (payload) => {
        var _a, _b;
        // To improve our UX, we display different loading messages while creating order
        (_a = actions.preCreationAction) === null || _a === void 0 ? void 0 : _a.call(actions);
        const loadingSetterPromise = skipPostCreationActions
            ? Promise.resolve()
            : setOrderFlowLoadingMessages([
                'Saving order...',
                'Sending order notifications...',
            ]);
        const completeOrderPromise = store.dispatch('orderFlow/completeOrder', {
            forceSaveAsDraft,
            readyForScheduling: payload === null || payload === void 0 ? void 0 : payload.readyForScheduling,
            queuedForScheduling: payload === null || payload === void 0 ? void 0 : payload.queuedForScheduling,
            skipPostCreationActions,
        });
        await Promise.all([
            loadingSetterPromise,
            completeOrderPromise,
        ]);
        (_b = actions.postCreationAction) === null || _b === void 0 ? void 0 : _b.call(actions);
        if (!skipPostCreationActions) {
            store.commit('orderFlow/SET_LOADING', {
                isLoading: false,
            });
        }
    };
    const { disableDraftWaypointScheduling } = store.state.variables.featureFlags;
    const isUserDoneEditingAction = async (isDoneEditing) => {
        forceSaveAsDraft = !isDoneEditing;
        const payload = {
            readyForScheduling: isDoneEditing,
        };
        await saveMaybeDraftOrder(payload);
    };
    /**
     * Timeout allows for alert to be displayed subsequently
     *
     * This prompt should only be shown when all required data for a full order
     * has been provided
     */
    const promptSendForScheduling = async (withTimeout) => {
        if (withTimeout) {
            await asyncTimeout(200);
        }
        const getConfirmButtonText = () => {
            if (disableDraftWaypointScheduling)
                return 'Yes';
            const order = store.state.orderFlow.draftOrder;
            if (order === null || order === void 0 ? void 0 : order.offlineScheduling)
                return 'Yes, send to ExamOne';
            return 'Yes, send for scheduling';
        };
        store.commit('orderFlow/SET_FLOW_ALERT_DATA', {
            title: copy.savingFullOrder.title,
            info: copy.savingFullOrder.info,
            cancelButtonText: copy.savingFullOrder.cancelButtonText,
            confirmButtonText: getConfirmButtonText(),
            confirmationAction: isUserDoneEditingAction,
            performActionOnCancel: true,
        });
    };
    /**
     * Since we disabled the ability to schedule draft orders in CARE-5256,
     * this should only be called for full orders
     */
    const maybePromptSendForScheduling = async (withTimeout) => {
        if (schedule === OrderFlowSchedulingEligibility.ALREADY_SENT_FOR_SCHEDULING
            || schedule === OrderFlowSchedulingEligibility.INELIGIBLE_FOR_SCHEDULING) {
            await saveMaybeDraftOrder();
            return;
        }
        // Do not await this call, so that the 1st alert can be dismissed
        // immediately, and the 2nd alert can be opened after the timeout.
        promptSendForScheduling(withTimeout);
    };
    const maybePromptIsUserDoneEditing = async () => {
        if (save === OrderFlowSaveEligibility.ELIGIBLE_FOR_SAVE_AS_FULL) {
            await maybePromptSendForScheduling();
            return;
        }
        const { draftOrder } = store.state.orderFlow;
        /** Draft order */
        const data = {
            title: copy.draftSaving.title,
            info: (draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.offlineScheduling) ? copy.draftSaving.infoForOfflineScheduling : copy.draftSaving.info,
            cancelButtonText: copy.draftSaving.cancelButtonText,
            confirmButtonText: copy.draftSaving.confirmButtonText,
            confirmationAction: async () => {
                await saveMaybeDraftOrder();
            },
        };
        store.commit('orderFlow/SET_FLOW_ALERT_DATA', data);
    };
    await maybePromptIsUserDoneEditing();
};
/**
 * If a completion action is defined, the next button will trigger that action
 * rather than being a `<router-link>` to the next step. While the async action
 * is in progress, a loader will show on the next button. If the promise
 * resolves, the user will be navigated to the next step.
 *
 * **To cancel navigation, an error must be thrown.** Errors thrown within these
 * actions will be shown to the user and prevent navigation to the next step.
 */
const orderFlowCompletionActions = {
    [OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW]: (_r) => saveOrder(),
};

export { getOrderFlowLastStepSaveCopy, getOrderFlowValidationEligibilities, orderFlowCompletionActions, saveOrder };
